package com.bikompanion.data.rating.firebase

import dev.gitlive.firebase.database.ServerValue
import dev.zacsweers.metro.Inject
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import com.bikompanion.domain.BikeIssue
import com.bikompanion.domain.BikeKey
import com.bikompanion.domain.BikeScopeSource
import kotlin.time.Instant

/**
 * Assembles one vote into the single atomic multi-path update that records it.
 *
 * Every path in the returned map either commits or none of them do, which is what keeps the log,
 * the cooldown marker and the counters from drifting apart. Counters are [ServerValue.increment]
 * rather than a read-modify-write transaction: an increment is resolved server-side before the
 * rules run, so two riders voting on the same bike at the same moment never contend.
 *
 * Monthly buckets rather than a rolling window of the last N votes — RTDB has no array type and
 * an increment is monotonic, so a window could only be trimmed by downloading the whole aggregate
 * on every vote. Buckets are also the better answer on their own merits: recency is the point,
 * and last-30-votes treats a bike rated thirty times a day like one rated three times a year.
 */
@Inject
class VoteUpdateBuilder {
    fun build(
        key: BikeKey,
        uid: String,
        voteId: String,
        isPositive: Boolean,
        isElectric: Boolean,
        issues: List<BikeIssue>,
        scopeSource: BikeScopeSource,
        castAtMs: Long,
    ): Map<String, Any?> {
        val scores = RatingPaths.scores(key)
        val bucket = monthBucketOf(castAtMs)
        val record = voteRecord(
            key = key,
            uid = uid,
            isPositive = isPositive,
            isElectric = isElectric,
            issues = issues,
            scopeSource = scopeSource,
            castAtMs = castAtMs,
        )
        val update = mutableMapOf<String, Any?>(
            "${RatingPaths.votes(key)}/$voteId" to record,
            // The same record again, filed under the rider instead of the bike. Two indexes over
            // one atomic write, because a rider cannot read their own history out of the log:
            // Realtime Database grants reads at or above the node queried, and the log is sharded
            // by bike, so listing one rider's votes would mean granting read on everyone's.
            "${RatingPaths.myVotes(uid)}/$voteId" to record,
            RatingPaths.guard(uid, key) to ServerValue.TIMESTAMP,
            "$scores/total" to ServerValue.increment(1.0),
            "$scores/months/$bucket/total" to ServerValue.increment(1.0),
            "$scores/lastVoteAt" to ServerValue.TIMESTAMP,
        )
        if (isPositive) {
            update["$scores/positive"] = ServerValue.increment(1.0)
            update["$scores/months/$bucket/positive"] = ServerValue.increment(1.0)
        }
        if (isElectric) {
            update["$scores/electric"] = ServerValue.increment(1.0)
        }
        issues.forEach { issue ->
            update["$scores/issues/${issue.name}"] = ServerValue.increment(1.0)
        }
        return update
    }

    private fun voteRecord(
        key: BikeKey,
        uid: String,
        isPositive: Boolean,
        isElectric: Boolean,
        issues: List<BikeIssue>,
        scopeSource: BikeScopeSource,
        castAtMs: Long,
    ): Map<String, Any?> =
        buildMap {
            put("uid", uid)
            put("networkId", key.networkId)
            put("bikeNumber", key.bikeNumber)
            put("isPositive", isPositive)
            put("isElectric", isElectric)
            // A keyed set rather than a list: RTDB has no array type and stores one as an object with
            // numeric keys anyway, and a Kotlin List does not survive the JS bindings — it reaches the
            // browser SDK as a non-array object and the write fails with "Invalid array length".
            if (issues.isNotEmpty()) put("issues", issues.associate { it.name to true })
            put("scopeSource", scopeSource.name)
            // The rules require createdAt == now, so a client clock can never be substituted. For a
            // vote that sat in the offline queue that means the flush time, which is why the moment it
            // was actually cast rides along separately.
            put("createdAt", ServerValue.TIMESTAMP)
            // Double, not Long: RTDB stores every number as a double, and a Kotlin Long reaches the
            // browser SDK as its emulated two-word object rather than a number. Epoch millis is far
            // below 2^53, so nothing is lost.
            put("castAtMs", castAtMs.toDouble())
        }

    /**
     * UTC, not the rider's zone: a bucket is shared by everyone who votes on a bike, so it has to
     * mean the same thing in Barcelona and in Auckland.
     */
    private fun monthBucketOf(epochMillis: Long): String {
        val date = Instant.fromEpochMilliseconds(epochMillis).toLocalDateTime(TimeZone.UTC).date
        return "${date.year.toString().padStart(4, '0')}-${date.month.number.toString().padStart(2, '0')}"
    }
}
